import { List } from 'immutable';
const rgbDecimalToHex = ({
  r,
  g,
  b
}) => {
  const toHex = decimal => {
    const hex = decimal.toString(16);
    const zeros = hex.length === 1 ? '0' : '';
    return `${zeros}${hex}`;
  };
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};
const hexToRgbDecimal = hexString => {
  const withoutHash = hexString.substring(1);
  const hexComponents = [];
  for (let i = 0; i < withoutHash.length; i += 2) {
    hexComponents.push(withoutHash.substring(i, i + 2));
  }
  const [r, g, b] = hexComponents;
  const toDecimal = v => parseInt(v, 16);
  return {
    r: toDecimal(r),
    g: toDecimal(g),
    b: toDecimal(b)
  };
};
const interpAToB = (aStart, aEnd, bStart, bEnd) => valueFromA => {
  const aRange = aEnd - aStart;
  const bRange = bEnd - bStart;
  const ratio = aRange === 0 ? 0 : bRange / aRange;
  return bStart + (valueFromA - aStart) * ratio;
};

/**
 * Generate interpolated colors based on gradient points (Array version)
 * @param steps Number of colors to generate
 * @param gradientPoints Array of color points with positions
 * @returns Array of hex color strings
 */
export const stepGradientColorsArray = (steps, gradientPoints) => {
  if (gradientPoints.length === 0) {
    return [];
  }
  if (steps <= 1) {
    return [gradientPoints[0].value];
  }
  const positionRatio = 1 / (steps - 1);
  const sortedGradientPoints = [...gradientPoints].sort((a, b) => a.positionFraction - b.positionFraction);
  const betweenPoints = positionFraction => {
    // find last
    const start = sortedGradientPoints.slice().reverse().find(point => positionFraction >= point.positionFraction);

    // find first
    const end = sortedGradientPoints.find(point => positionFraction <= point.positionFraction);
    return [start, end];
  };
  const colors = [];
  for (let i = 0; i < steps; i++) {
    const position = i * positionRatio;
    const [point1, point2] = betweenPoints(position);
    if (point1 === undefined) {
      colors.push(point2.value);
      continue;
    }
    if (point2 === undefined) {
      colors.push(point1.value);
      continue;
    }
    const point1RGB = hexToRgbDecimal(point1.value);
    const point2RGB = hexToRgbDecimal(point2.value);
    const interpR = interpAToB(point1.positionFraction, point2.positionFraction, point1RGB.r, point2RGB.r);
    const interpG = interpAToB(point1.positionFraction, point2.positionFraction, point1RGB.g, point2RGB.g);
    const interpB = interpAToB(point1.positionFraction, point2.positionFraction, point1RGB.b, point2RGB.b);
    const newColorRgb = {
      r: Math.round(interpR(position)),
      g: Math.round(interpG(position)),
      b: Math.round(interpB(position))
    };
    colors.push(rgbDecimalToHex(newColorRgb));
  }
  return colors;
};

/**
 * Generate interpolated colors based on gradient points (Immutable List version)
 * @param steps Number of colors to generate
 * @param gradientPoints Immutable List of color points with positions
 * @returns Immutable List of hex color strings
 */
export const stepGradientColors = (steps, gradientPoints) => {
  const result = stepGradientColorsArray(steps, gradientPoints.toJS());
  return List(result);
};